import React from "react";
import { ExternalLink, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { projectsData } from "../data/projectsData";

const Projects = ({ isDarkMode }) => {
  const navigate = useNavigate();
  // Filter featured projects. Since all are initially true, this will show all.
  // You can toggle isFeatured in the data file to control what shows on the home page.
  const featuredProjects = projectsData.filter(project => project.isFeatured);

  return (
    <section
      id="projects"
      className={`py-10 transition-all duration-500 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-5xl md:text-6xl font-bold mb-6 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Featured Projects
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6"></div>
          <p
            className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Here are some of my recent projects that showcase my skills and
            creativity
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredProjects.map((project, index) => (
            <div
              key={index}
              className={`group rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                isDarkMode
                  ? "bg-gray-900 shadow-xl border border-gray-700"
                  : "bg-white shadow-xl"
              }`}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden h-48">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                  <div className="text-center">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:scale-110 transition-transform duration-300 flex items-center gap-2 mx-auto"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Project
                    </a>
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="p-4">
                <div
                  className={`text-sm font-bold mb-2 bg-gradient-to-r ${project.gradient} bg-clip-text text-transparent`}
                >
                  {project.category}
                </div>
                <h3
                  className={`text-2xl font-bold mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {project.title}
                </h3>
                <p
                  className={`mb-4 leading-relaxed ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mt-4">
                  {project.technologies.map((tech, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-800 text-blue-300 border border-gray-700 hover:bg-gray-700"
                          : "bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100"
                      }`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate("/allProjects")}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
              isDarkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-900/50"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200"
            }`}
          >
            See More Projects
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
